import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { FileViewerComponent } from '../../../../shared/components/file-viewer/file-viewer.component';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { UserFlagDetailsComponent } from '../user-flag-details/user-flag-details.component';
import { ReportsService } from '../../../../shared/services/reports/reports.service';
import { FileUploadService } from '../../../documents/services/file-upload.service';
import { AuthService } from '../../../auth/services/auth.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  EditForm,
  EntityOptions,
  EntityType,
  Report,
  ReportEntityDetails,
  Reporter,
  SeverityOptions,
  StatusOptions,
  UserReportFiles,
} from '../../../../shared/interfaces/report-content';
import { formatNotification } from '../../../notifications/utils/notification-formatter';
import {
  NotificationFormatInput,
  NotificationTypes,
} from '../../../notifications/models/notifications';

@Component({
  selector: 'app-user-flags',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageSizeComponent,
    PaginationComponent,
    TableComponent,
    DatePipe,
    SecondaryPopupComponent,
    FileViewerComponent,
    RecordDetailsComponent,
    UserFlagDetailsComponent,
  ],
  templateUrl: './user-flags.component.html',
  styleUrl: './user-flags.component.scss',
})
export class UserFlagsComponent extends MenuComponentBase implements OnInit {
  private reportsService = inject(ReportsService);
  private fileUploadService = inject(FileUploadService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  readonly isAdminUser = this.authService.hasPermission(
    PermissionCode.ADMIN_PRIVILEGES,
  );

  isManageFlags = false;

  currentPage = 1;
  pageSize = 50;
  totalReports = 0;
  loading = false;

  reports: Report[] = [];

  selectedReport: Report | null = null;
  showReportDetails = false;
  isEditMode = false;

  editForm: EditForm = {
    severity: null,
    status: null,
    resolution_notes: '',
  };

  selectedFile: UserReportFiles | null = null;
  showFileViewer = false;

  entityDetails: ReportEntityDetails | null = null;
  showRecordDetails = false;

  readonly entityType = EntityType;

  // Filters
  severityFilter: number | null = null;
  statusFilter: string | null = null;
  entityTypeFilter: string | null = null;

  readonly severityOptions = SeverityOptions;
  readonly statusOptions = StatusOptions;
  readonly entityTypeOptions = EntityOptions;

  columns: TableColumn[] = [];

  override ngOnInit(): void {
    super.ngOnInit();
    this.isManageFlags =
      this.activatedRoute.snapshot.routeConfig?.path === 'manage-flags' ||
      this.router.url.includes('manage-flags');

    this.initColumns();
    this.resetEditForm();

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.entityTypeFilter = params['entity_type'] || null;
        this.severityFilter =
          params['severity'] !== undefined &&
          params['severity'] !== null &&
          params['severity'] !== ''
            ? Number(params['severity'])
            : null;
        this.statusFilter = params['status'] || null;

        this.loadReports();
      });
  }

  private initColumns(): void {
    if (this.isManageFlags) {
      this.columns = [
        {
          header: 'Entity Type',
          field: 'entity_type',
          width: '140px',
        },
        {
          header: 'Reported By',
          field: 'reporter',
          width: '180px',
        },
        {
          header: 'Reason',
          field: 'reason_label',
          width: '180px',
        },
        {
          header: 'Severity',
          field: 'severity',
          width: '130px',
        },
        {
          header: 'Status',
          field: 'status',
          width: '130px',
        },
        {
          header: 'Created At',
          field: 'created_at',
          width: '180px',
        },
        {
          header: '',
          field: 'view',
          width: '60px',
        },
      ];
    } else {
      this.columns = [
        {
          header: 'Entity Type',
          field: 'entity_type',
          width: '150px',
        },
        {
          header: 'Reason',
          field: 'reason_label',
          width: '180px',
        },
        {
          header: 'Severity',
          field: 'severity',
          width: '150px',
        },
        {
          header: 'Status',
          field: 'status',
          width: '150px',
        },
        {
          header: 'Created At',
          field: 'created_at',
          width: '200px',
        },
        {
          header: '',
          field: 'view',
          width: '60px',
        },
      ];
    }
  }

  private updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        entity_type: this.entityTypeFilter || null,
        severity: this.severityFilter ?? null,
        status: this.statusFilter || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  loadReports(): void {
    this.loading = true;

    const request$ = this.isManageFlags
      ? this.reportsService.getAllReports(
          this.currentPage,
          this.pageSize,
          this.entityTypeFilter,
          this.severityFilter,
          this.statusFilter,
        )
      : this.reportsService.getUserReports(
          this.currentPage,
          this.pageSize,
          this.entityTypeFilter,
          this.severityFilter,
          this.statusFilter,
        );

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: PaginatedResponse<Report>) => {
        this.reports = response.results;
        this.totalReports = response.count;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadReports();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadReports();
    }
  }

  onEntityTypeFilterChange(value: string | null): void {
    this.entityTypeFilter = value;
    this.currentPage = 1;
    this.updateQueryParams();
  }

  onStatusFilterChange(value: string | null): void {
    this.statusFilter = value;
    this.currentPage = 1;
    this.updateQueryParams();
  }

  onSeverityFilterChange(value: number | null): void {
    this.severityFilter = value;
    this.currentPage = 1;
    this.updateQueryParams();
  }

  get hasActiveFilters(): boolean {
    return (
      this.entityTypeFilter !== null ||
      this.statusFilter !== null ||
      this.severityFilter !== null
    );
  }

  get activeFiltersCount(): number {
    let count = 0;
    if (this.entityTypeFilter !== null) count++;
    if (this.statusFilter !== null) count++;
    if (this.severityFilter !== null) count++;
    return count;
  }

  resetFilters(): void {
    this.entityTypeFilter = null;
    this.statusFilter = null;
    this.severityFilter = null;
    this.currentPage = 1;
    this.updateQueryParams();
  }

  viewReport(report: Report): void {
    this.selectedReport = report;
    this.isEditMode = false;
    this.resetEditForm();

    if (!this.isManageFlags) {
      this.entityDetails = null;
      this.showReportDetails = true;
      return;
    }

    if (report.entity_type === EntityType.BUSINESS) {
      this.entityDetails = null;
      this.showReportDetails = true;
      return;
    }

    this.entityDetails = null;
    this.reportsService
      .getEntityDetails(report.entity_type, report.entity_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (report.entity_type === EntityType.NOTIFICATION && res?.details) {
            const notificationDetails = res.details;
            const notification: NotificationFormatInput = {
              notification_type: Number(
                notificationDetails['notification_type'],
              ) as NotificationTypes,
              message_data: notificationDetails[
                'message_data'
              ] as unknown as Record<string, unknown>,
            };

            const formattedNotification = formatNotification(notification);

            this.entityDetails = {
              ...res,
              details: {
                ...res.details,
                title: formattedNotification.title,
                body: formattedNotification.body,
              },
            };
          } else {
            this.entityDetails = res;
          }

          this.showReportDetails = true;
        },
        error: (err) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              err?.error?.detail ||
              err?.error?.message ||
              'Failed to fetch report details',
          });
          this.showReportDetails = true;
        },
      });
  }

  closeReportDetails(isClickedOutside: boolean): void {
    if (!isClickedOutside) {
      this.showReportDetails = false;
      this.selectedReport = null;
      this.entityDetails = null;
      this.isEditMode = false;
      this.resetEditForm();
    }
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.resetEditForm();
  }

  onPopupPrimaryAction(): void {
    if (this.isEditMode) {
      this.saveReportChanges();
      return;
    }

    if (!this.selectedReport) {
      return;
    }

    this.isEditMode = true;
    this.setEditForm();
    this.scrollToPopupBottom();
  }

  private scrollToPopupBottom(): void {
    setTimeout(() => {
      const popupDataWrapper = document.querySelector('.popup-data-wrapper');
      if (popupDataWrapper) {
        popupDataWrapper.scrollTo({
          top: popupDataWrapper.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 50);
  }

  private setEditForm(): void {
    if (this.selectedReport) {
      this.editForm = {
        severity: this.selectedReport.severity,
        status: this.selectedReport.status,
        resolution_notes: this.selectedReport.resolution_notes || '',
      };
    } else {
      this.resetEditForm();
    }
  }

  private resetEditForm(): void {
    this.editForm = {
      severity: null,
      status: null,
      resolution_notes: '',
    };
  }

  saveReportChanges(): void {
    if (!this.selectedReport) {
      return;
    }

    const payload: Partial<Report> = {};

    if (
      this.editForm.status !== null &&
      this.editForm.status !== this.selectedReport.status
    ) {
      payload.status = this.editForm.status;
    }

    const currentResolutionNotes = this.editForm.resolution_notes?.trim() ?? '';
    const originalResolutionNotes =
      this.selectedReport.resolution_notes?.trim() ?? '';

    if (currentResolutionNotes !== originalResolutionNotes) {
      payload.resolution_notes = currentResolutionNotes;
    }

    if (Object.keys(payload).length === 0) {
      this.isEditMode = false;
      return;
    }

    this.reportsService
      .updateReport(this.selectedReport.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Report updated successfully',
          });

          this.closeReportDetails(false);
          this.loadReports();
        },
        error: (err) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              err?.error?.detail ||
              err?.error?.message ||
              'Failed to update report',
          });
        },
      });
  }

  getSeverityClass(severity: number | string | null | undefined): string {
    if (typeof severity === 'number') {
      switch (severity) {
        case 1:
          return 'severity-low';
        case 2:
          return 'severity-medium';
        case 3:
          return 'severity-high';
        case 4:
          return 'severity-critical';
        default:
          return '';
      }
    }
    switch (severity?.toLowerCase()) {
      case 'low':
        return 'severity-low';
      case 'medium':
        return 'severity-medium';
      case 'high':
        return 'severity-high';
      case 'critical':
        return 'severity-critical';
      default:
        return '';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'submitted':
        return 'status-submitted';
      case 'under_review':
        return 'status-under-review';
      case 'resolved':
        return 'status-resolved';
      case 'dismissed':
        return 'status-dismissed';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'submitted':
        return 'Submitted';
      case 'under_review':
        return 'Under Review';
      case 'resolved':
        return 'Resolved';
      case 'dismissed':
        return 'Dismissed';
      default:
        return status;
    }
  }

  getReporterFullName(reporter: Reporter | undefined | null): string {
    if (!reporter) {
      return '';
    }
    if (reporter.first_name && reporter.last_name) {
      return `${reporter.first_name} ${reporter.last_name}`;
    }
    return reporter.first_name || reporter.last_name || reporter.email || '';
  }

  openFileViewer(file: UserReportFiles): void {
    if (!file?.file_id) {
      return;
    }

    this.selectedFile = null;
    this.showFileViewer = false;

    this.fileUploadService
      .downloadFile(file.file_id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.selectedFile = {
          ...file,
          download_url: response.download_url,
        };

        this.showFileViewer = true;
      });
  }

  downloadFile(file: UserReportFiles): void {
    this.fileUploadService
      .downloadFile(file.file_id, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = response.file_name;
        link.click();
      });
  }

  onCloseViewer(): void {
    this.showFileViewer = false;
    this.selectedFile = null;
  }

  downloadSelectedFile(): void {
    if (!this.selectedFile) {
      return;
    }

    this.downloadFile(this.selectedFile);
  }

  openEntityFileViewer(): void {
    if (!this.entityDetails?.details) {
      return;
    }

    const file: UserReportFiles = {
      file_id: Number(this.entityDetails.entity_id),
      file_name: this.entityDetails.details['file_name'],
      file_url: this.entityDetails.details['azure_reference'],
    };

    this.openFileViewer(file);
  }

  downloadEntityFile(): void {
    if (!this.entityDetails?.details) {
      return;
    }

    const file: UserReportFiles = {
      file_id: Number(this.entityDetails.entity_id),
      file_name: this.entityDetails.details['file_name'],
      file_url: this.entityDetails.details['azure_reference'],
    };

    this.downloadFile(file);
  }

  get recordUniqueId(): string {
    if (this.selectedReport?.entity_type === EntityType.BUSINESS) {
      return this.selectedReport.entity_id.toString();
    }

    if (
      this.selectedReport?.entity_type === EntityType.NOTE &&
      this.entityDetails?.details
    ) {
      return this.entityDetails?.details['unique_id'];
    }
    return '';
  }
}
